import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { useEmployeeAttendanceAuth } from '../../employee-attendance/context/EmployeeAttendanceAuthContext';

const STORAGE_KEY = 'utilities_company_id';

const UtilitiesCompanyContext = createContext(null);

function readStoredCompanyId() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? Number(raw) : null;
  } catch {
    return null;
  }
}

function writeStoredCompanyId(id) {
  try {
    localStorage.setItem(STORAGE_KEY, String(id));
  } catch {
    /* storage can be blocked; the selection still works for this page view */
  }
}

/**
 * Holds the company the Utilities pages are working in. The list comes from
 * the login response (user.utilities.companies); the choice is remembered.
 */
export function UtilitiesCompanyProvider({ children }) {
  const { user } = useEmployeeAttendanceAuth();
  const companies = useMemo(() => user?.utilities?.companies || [], [user]);
  const defaultCompanyId = user?.utilities?.default_company_id ?? companies[0]?.id ?? null;

  const [storedId, setStoredId] = useState(readStoredCompanyId);

  const companyId = useMemo(() => {
    const exists = (id) => companies.some((c) => Number(c.id) === Number(id));
    if (storedId && exists(storedId)) return Number(storedId);
    if (defaultCompanyId && exists(defaultCompanyId)) return Number(defaultCompanyId);
    return companies[0]?.id ?? null;
  }, [companies, defaultCompanyId, storedId]);

  const company = useMemo(
    () => companies.find((c) => Number(c.id) === Number(companyId)) || null,
    [companies, companyId]
  );

  const setCompanyId = useCallback((id) => {
    writeStoredCompanyId(id);
    setStoredId(Number(id));
  }, []);

  const value = useMemo(
    () => ({ companies, company, companyId, setCompanyId }),
    [companies, company, companyId, setCompanyId]
  );

  return (
    <UtilitiesCompanyContext.Provider value={value}>{children}</UtilitiesCompanyContext.Provider>
  );
}

export function useUtilitiesCompany() {
  const ctx = useContext(UtilitiesCompanyContext);
  if (!ctx) throw new Error('useUtilitiesCompany must be used inside UtilitiesCompanyProvider');
  return ctx;
}
